import React, { useMemo, useState } from 'react'
import { Box, Typography, Chip, IconButton, Radio, Checkbox } from '@mui/material'

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'

import DropzonePreview from '../components/survey/utils/DropzonePreview'
import ImageGallery from '../forms/surveys/components/ImageGallery'
import { useImageGallery } from '../forms/surveys/hooks/useImageGallery'
import { TASK_TYPES } from '../constants/constants'
import {
  buildSurveyTree,
  mergeSurveyReportData,
  shouldShowConditionalOption
} from '../task_details/surveyReportHelpers'
import {
  SURVEY_TYPES,
  SURVEY_TYPE_LABELS,
  QUESTION_CARD_VARIANTS,
  BASE_QUESTION_CARD_TEXTS,
  CHOICE_OPTION_CONTROL_SX,
  SINGLE_CHOICE_CARD_STYLES,
  SINGLE_CHOICE_CARD_TEXTS,
  SURVEY_QUESTION_CHROME,
  SURVEY_REPORT_STYLES,
  SURVEY_REPORT_TEXTS,
  getSurveyQuestionChrome
} from '../components/survey/constants/constantsSurveys'
import {
  cardContainerSx,
  headerSx,
  chipSx,
  collapseButtonSx,
  collapseIconSx
} from '../components/survey/style/questionCardStyles'

const NO_IMAGE = 'NO_IMAGE'

const READ_ONLY_CARD_SX = {
  ...cardContainerSx,
  maxWidth: '100%',
  borderRadius: '6px'
}

const READ_ONLY_HEADER_PADDING = {
  xs: '8px 12px',
  sm: '10px 16px'
}

const READ_ONLY_FIELD_SX = {
  backgroundColor: SURVEY_QUESTION_CHROME.fieldBackground,
  borderRadius: '4px',
  padding: '11px 14px',
  fontSize: 13,
  lineHeight: 1.45,
  color: SURVEY_QUESTION_CHROME.fieldText,
  wordBreak: 'break-word'
}

const READ_ONLY_DESCRIPTION_SX = READ_ONLY_FIELD_SX

const READ_ONLY_ANSWER_SX = {
  ...READ_ONLY_FIELD_SX,
  color: SURVEY_QUESTION_CHROME.placeholderText
}

const READ_ONLY_BODY_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  px: {
    xs: 1.5,
    sm: 2
  },
  py: 1.5
}

const READ_ONLY_OPTIONS_CONTAINER_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1
}

const READ_ONLY_OPTION_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  minWidth: 0,
  backgroundColor: SURVEY_QUESTION_CHROME.fieldBackground,
  border: '1px solid transparent',
  borderRadius: '4px',
  padding: '11px 14px'
}

const READ_ONLY_OPTION_TEXT_SX = {
  fontSize: 13,
  color: SURVEY_QUESTION_CHROME.fieldText,
  wordBreak: 'break-word'
}

const REPORT_SELECTED_OPTION_ROW_SX = {
  backgroundColor: SURVEY_REPORT_STYLES.selectedOptionBackgroundColor,
  borderColor: SURVEY_REPORT_STYLES.selectedOptionBorderColor
}

const REPORT_OPTION_CONTROL_SX = {
  ...CHOICE_OPTION_CONTROL_SX,
  '&.Mui-checked': {
    color: SURVEY_REPORT_STYLES.selectedOptionControlColor
  }
}

const REPORT_ANSWER_LABEL_SX = {
  fontSize: 12,
  fontWeight: 600,
  color: SURVEY_QUESTION_CHROME.titleColor,
  mt: 0.5,
  mb: 0.5
}

const REPORT_ANSWER_VALUE_SX = READ_ONLY_FIELD_SX

const REPORT_DESCRIPTION_SX = {
  ...READ_ONLY_FIELD_SX,
  color: SURVEY_QUESTION_CHROME.placeholderText
}

const CONDITIONAL_CHIP_SX = {
  ...chipSx,
  backgroundColor: SINGLE_CHOICE_CARD_STYLES.conditionalChipBackgroundColor,
  color: SINGLE_CHOICE_CARD_STYLES.conditionalChipTextColor,
  fontWeight: SINGLE_CHOICE_CARD_STYLES.conditionalChipFontWeight
}

const READ_ONLY_COLLAPSE_BUTTON_SX = {
  ...collapseButtonSx,
  mr: 0,
  p: 0.25
}

const READ_ONLY_COLLAPSE_ICON_FONT_SIZE = {
  xs: 20,
  sm: 24
}

const PARENT_OPTION_LABEL_SX = {
  fontSize: 12,
  fontWeight: 500,
  fontStyle: 'italic',
  color: SURVEY_QUESTION_CHROME.branchLabel,
  wordBreak: 'break-word'
}

const QUESTION_TITLE_SX = {
  fontSize: SURVEY_REPORT_STYLES.questionTitleFontSize,
  fontWeight: SURVEY_REPORT_STYLES.questionTitleFontWeight,
  lineHeight: SURVEY_REPORT_STYLES.questionTitleLineHeight,
  color: SURVEY_REPORT_STYLES.questionTitleColor,
  minWidth: 0,
  flex: 1,
  whiteSpace: 'normal',
  wordBreak: 'break-word'
}

const QUESTION_META_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flex: 'none'
}

const getBranchSx = (reportMode) => ({
  mt: 0.25,
  mb: reportMode ? '12px' : '10px',
  ml: reportMode
    ? SURVEY_QUESTION_CHROME.reportBranchMarginLeft
    : SURVEY_QUESTION_CHROME.detailBranchMarginLeft,
  pl: SURVEY_QUESTION_CHROME.branchPaddingLeft,
  borderLeft: `3px solid ${SURVEY_QUESTION_CHROME.branchBorder}`
})

function getDisplayIndexValue(question = {}) {
  return question?.display_index || question?.displayIndex || ''
}

function getResolvedDisplayIndexValue(question = {}) {
  return question?.resolved_display_index || getDisplayIndexValue(question)
}

function hasConditionalChildren(question = {}) {
  return (question?.options || []).some((option) => (option.childQuestions || []).length > 0)
}

function isChoiceQuestion(question = {}) {
  return (
    question?.type === SURVEY_TYPES.singleChoice || question?.type === SURVEY_TYPES.multipleChoice
  )
}

function ConditionalBranch({ option, reportMode, reportContext }) {
  const childQuestions = option.childQuestions || []

  return (
    <Box sx={getBranchSx(reportMode)}>
      {childQuestions.map((childQuestion, childIndex) => (
        <Box
          key={childQuestion.uid || childQuestion.id || childIndex}
          sx={{ mb: childIndex === childQuestions.length - 1 ? 0 : 1.5 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 0.75
            }}
          >
            <SubdirectoryArrowRightIcon
              sx={{
                fontSize: 16,
                color: SURVEY_QUESTION_CHROME.branchLabel,
                flexShrink: 0
              }}
            />

            <Typography sx={PARENT_OPTION_LABEL_SX}>
              {reportMode
                ? SURVEY_REPORT_TEXTS.parentOptionPrefix
                : BASE_QUESTION_CARD_TEXTS.parentOptionPrefix}
              {option.text}
              {BASE_QUESTION_CARD_TEXTS.quoteSuffix}
            </Typography>
          </Box>

          <SurveyQuestionCard
            question={childQuestion}
            isNested
            reportMode={reportMode}
            reportContext={reportContext}
          />
        </Box>
      ))}
    </Box>
  )
}

function QuestionOptions({ question, reportMode, reportContext }) {
  const options = question?.options || []
  const Control = question?.type === SURVEY_TYPES.multipleChoice ? Checkbox : Radio
  const optionsWithBranch = options.filter(
    (option) =>
      (option.childQuestions || []).length > 0 && shouldShowConditionalOption(option, reportMode)
  )

  return (
    <Box sx={READ_ONLY_OPTIONS_CONTAINER_SX}>
      {options.map((option, optionIndex) => {
        const isSelectedAnswer = reportMode && Boolean(option.selected)

        return (
          <Box key={option.id || optionIndex}>
            <Box
              sx={{
                ...READ_ONLY_OPTION_ROW_SX,
                ...(isSelectedAnswer ? REPORT_SELECTED_OPTION_ROW_SX : {})
              }}
            >
              <Control
                checked={isSelectedAnswer}
                onChange={() => {}}
                size="small"
                tabIndex={-1}
                sx={reportMode ? REPORT_OPTION_CONTROL_SX : CHOICE_OPTION_CONTROL_SX}
              />

              <Typography sx={READ_ONLY_OPTION_TEXT_SX}>{option.text}</Typography>
            </Box>

            {/* Fuera del reporte cada rama sigue debajo de su propia opción. */}
            {!reportMode && optionsWithBranch.includes(option) && (
              <ConditionalBranch
                option={option}
                reportMode={reportMode}
                reportContext={reportContext}
              />
            )}
          </Box>
        )
      })}

      {reportMode &&
        optionsWithBranch.map((option, optionIndex) => (
          <ConditionalBranch
            key={option.id || `branch-${optionIndex}`}
            option={option}
            reportMode={reportMode}
            reportContext={reportContext}
          />
        ))}
    </Box>
  )
}

function SurveyReportImages({ images, uid, questionNumber }) {
  const {
    imageGallery,
    maxImageInWrapper,
    overflowingImageAnswerAmount,
    showImage,
    currentImageShow,
    filenameForDownloads,
    handleShowImage,
    handleImageClick
  } = useImageGallery(images, uid, questionNumber, TASK_TYPES.survey.key)

  if (!images?.length) return null

  return (
    <ImageGallery
      imageGallery={imageGallery}
      maxImageInWrapper={maxImageInWrapper}
      overflowingImageAnswerAmount={overflowingImageAnswerAmount}
      showImage={showImage}
      currentImageShow={currentImageShow}
      filenameForDownloads={filenameForDownloads}
      onImageClick={handleImageClick}
      onCloseLightbox={() => handleShowImage(false)}
      imageAnswer={images}
      taskType={TASK_TYPES.survey.key}
    />
  )
}

function SurveyQuestionCard({
  question,
  isNested = false,
  reportMode = false,
  reportContext = {}
}) {
  const [collapsed, setCollapsed] = useState(true)

  const isConditionalParent = hasConditionalChildren(question)
  const headerChrome = getSurveyQuestionChrome(isConditionalParent, isNested)
  const displayIndex = getResolvedDisplayIndexValue(question)
  const typeLabel = SURVEY_TYPE_LABELS[question?.type] || ''
  const answerPlaceholder = QUESTION_CARD_VARIANTS[question?.type]?.answerPlaceholder || ''
  const shouldShowImageBox =
    question?.type === SURVEY_TYPES.image || question?.image_requirement !== NO_IMAGE
  const showAnswerField = !isChoiceQuestion(question) && question?.type !== SURVEY_TYPES.image
  const questionTitle = `${displayIndex ? `${displayIndex}: ` : ''}${question?.question || ''}`

  return (
    <Box
      sx={{
        ...READ_ONLY_CARD_SX,
        border: `1px solid ${headerChrome.borderColor}`
      }}
    >
      <Box
        sx={{
          ...headerSx(collapsed),
          padding: READ_ONLY_HEADER_PADDING,
          minHeight: {
            xs: 44,
            sm: 52
          },
          backgroundColor: headerChrome.backgroundColor,
          borderBottom: collapsed ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 3
        }}
      >
        <Typography sx={QUESTION_TITLE_SX}>{questionTitle}</Typography>

        <Box sx={QUESTION_META_SX}>
          <Chip label={typeLabel} size="small" sx={chipSx} />

          {isConditionalParent && (
            <Chip
              label={SINGLE_CHOICE_CARD_TEXTS.hasConditionalsChip}
              size="small"
              sx={CONDITIONAL_CHIP_SX}
            />
          )}

          <IconButton
            onClick={() => setCollapsed((prev) => !prev)}
            disableRipple
            aria-label={collapsed ? 'Abrir pregunta' : 'Cerrar pregunta'}
            sx={READ_ONLY_COLLAPSE_BUTTON_SX}
          >
            <KeyboardArrowUpIcon
              sx={{
                ...collapseIconSx(collapsed),
                fontSize: READ_ONLY_COLLAPSE_ICON_FONT_SIZE
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {!collapsed && (
        <Box sx={READ_ONLY_BODY_SX}>
          {question?.descriptions ? (
            <Box sx={reportMode ? REPORT_DESCRIPTION_SX : READ_ONLY_DESCRIPTION_SX}>
              {question.descriptions}
            </Box>
          ) : null}

          {showAnswerField && reportMode ? (
            <Box>
              <Typography sx={REPORT_ANSWER_LABEL_SX}>{SURVEY_REPORT_TEXTS.answerLabel}</Typography>

              <Box sx={REPORT_ANSWER_VALUE_SX}>
                {question?.response || SURVEY_REPORT_TEXTS.emptyAnswer}
              </Box>
            </Box>
          ) : null}

          {showAnswerField && !reportMode && answerPlaceholder ? (
            <Box sx={READ_ONLY_ANSWER_SX}>{answerPlaceholder}</Box>
          ) : null}

          {isChoiceQuestion(question) && (
            <QuestionOptions
              question={question}
              reportMode={reportMode}
              reportContext={reportContext}
            />
          )}

          {reportMode ? (
            <SurveyReportImages
              images={question?.image_answer}
              uid={reportContext.uid}
              questionNumber={displayIndex}
            />
          ) : (
            shouldShowImageBox && <DropzonePreview />
          )}
        </Box>
      )}
    </Box>
  )
}

export default function PosDetailsSurvey({
  data = [],
  responses = [],
  surveyItems = [],
  images = [],
  uid = '',
  reportMode = false
}) {
  const mergedData = useMemo(
    () =>
      reportMode
        ? mergeSurveyReportData({
            definitions: data,
            responses,
            surveyItems,
            fallbackImages: images
          })
        : data,
    [data, images, reportMode, responses, surveyItems]
  )
  const surveyTree = useMemo(() => buildSurveyTree(mergedData), [mergedData])

  if (!surveyTree.length) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {surveyTree.map((question, index) => (
        <SurveyQuestionCard
          key={question.uid || question.id || index}
          question={question}
          reportMode={reportMode}
          reportContext={{ uid }}
        />
      ))}
    </Box>
  )
}
